import type { Area } from 'react-easy-crop';

const MAX_SIZE = 800;
const JPEG_QUALITY = 0.9;

/**
 * Crops an image to the specified area and returns a compressed JPEG blob.
 * Output is capped at 800x800px for profile photos.
 */
export async function getCroppedImg(imageSrc: string, cropArea: Area): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  // Cap output size
  const size = Math.min(cropArea.width, cropArea.height, MAX_SIZE);
  canvas.width = size;
  canvas.height = size;

  ctx.drawImage(
    image,
    cropArea.x,
    cropArea.y,
    cropArea.width,
    cropArea.height,
    0,
    0,
    size,
    size,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob failed'));
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}
